/**
 * Complimentary Mystery Tee — Marditarl Co.
 *
 * Standalone add-on. It does not import, change or replace any theme code.
 * It only listens for the theme's "cart changed" signal and talks to
 * Shopify's standard cart endpoints.
 *
 * What it does:
 *   1. Adds the gift as soon as the customer chooses a size. There is no button.
 *   2. Counts only products tagged tee-pair towards the three-item minimum.
 *   3. Removes the gift whenever the cart stops qualifying.
 *   4. Never lets the gift be charged for, and never lets there be two.
 *
 * The product, minimum and qualifying tag are set in snippets/mystery-gift.liquid.
 */
(function () {
  if (window.__mysteryGiftLoaded) return;
  window.__mysteryGiftLoaded = true;

  const tag = document.querySelector('script[data-mystery-gift-script]');
  const HANDLE = tag?.dataset.handle || '';
  const MINIMUM = parseInt(tag?.dataset.minimum || '', 10) || 3;
  const QUALIFYING_TAG = (tag?.dataset.qualifyingTag || 'tee-pair').trim().toLowerCase();
  if (!HANDLE) return;

  const SOURCE = 'mystery-gift';
  const CART_EVENT = 'cart:update';
  const ROOT = window.Shopify?.routes?.root || '/';

  const TEXT = {
    adding: 'Adding your Mystery Tee…',
    unavailable: 'Sorry, that size isn’t available right now. Please try another.',
    notFree:
      'Sorry, the complimentary Mystery Tee can’t be applied to this order, so it hasn’t been added.',
  };

  let busy = false;
  let queued = false;
  let timer = 0;
  let notice = '';

  /* ---------- Shopify cart ---------- */

  async function request(path, body) {
    const options = body
      ? {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(body),
        }
      : {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        };

    const response = await fetch(`${ROOT}${path}`, {
      credentials: 'same-origin',
      ...options,
    });

    let data = null;

    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    if (!response.ok || !data) {
      throw new Error(
        `${path} failed: ${data?.description || data?.message || response.status}`
      );
    }

    return data;
  }

  /**
   * Ask Shopify to return refreshed cart HTML with the change.
   */
  function withSections(body) {
    const ids = new Set();

    document.querySelectorAll('cart-items-component').forEach((element) => {
      if (element instanceof HTMLElement && element.dataset.sectionId) {
        ids.add(element.dataset.sectionId);
      }
    });

    if (!ids.size) return body;

    return {
      ...body,
      sections: Array.from(ids).slice(0, 5).join(','),
      sections_url: window.location.pathname,
    };
  }

  /* ---------- Qualification ---------- */

  const qualifyingCache = new Map();

  async function isQualifying(item) {
    if (!item?.handle || item.handle === HANDLE) return false;

    if (!qualifyingCache.has(item.handle)) {
      const check = request(`products/${encodeURIComponent(item.handle)}.js`)
        .then((product) => {
          const tags = Array.isArray(product.tags)
            ? product.tags
            : String(product.tags || '')
                .split(',')
                .map((value) => value.trim())
                .filter(Boolean);

          return tags.some(
            (value) => String(value).trim().toLowerCase() === QUALIFYING_TAG
          );
        })
        .catch((error) => {
          qualifyingCache.delete(item.handle);
          throw error;
        });

      qualifyingCache.set(item.handle, check);
    }

    return qualifyingCache.get(item.handle);
  }

  async function inspect(cart) {
    const gifts = cart.items.filter((item) => item.handle === HANDLE);
    const candidates = cart.items.filter((item) => item.handle !== HANDLE);

    const checks = await Promise.all(
      candidates.map(async (item) => ({
        quantity: item.quantity,
        qualifies: await isQualifying(item),
      }))
    );

    const qualifying = checks.reduce(
      (total, item) => total + (item.qualifies ? item.quantity : 0),
      0
    );

    return {
      gifts,
      qualifying,
    };
  }

  /* ---------- The rules ---------- */

  /**
   * Makes the cart obey the offer:
   *   fewer than the minimum -> no gift
   *   otherwise              -> at most one gift, and only if it is free
   */
  async function reconcile() {
    let cart = await request('cart.js');
    let sections;
    let changed = false;
    let reason = '';

    const { gifts, qualifying } = await inspect(cart);

    if (!gifts.length) {
      return {
        cart,
        sections,
        changed,
        reason,
      };
    }

    const updates = {};

    if (qualifying < MINIMUM) {
      gifts.forEach((gift) => {
        updates[gift.key] = 0;
      });

      reason = 'below-minimum';
    } else {
      gifts.forEach((gift, index) => {
        if (index > 0) {
          updates[gift.key] = 0;
        } else if (gift.quantity !== 1) {
          updates[gift.key] = 1;
        }
      });

      if (Object.keys(updates).length) {
        reason = 'duplicate';
      }
    }

    if (Object.keys(updates).length) {
      lock(true);

      cart = await request(
        'cart/update.js',
        withSections({
          updates,
        })
      );

      sections = cart.sections;
      changed = true;
    }

    /**
     * Final safeguard:
     * if Shopify has not made the gift free, remove it.
     */
    const remaining = (await inspect(cart)).gifts;

    if (remaining.some((gift) => gift.final_line_price > 0)) {
      const removal = {};

      remaining.forEach((gift) => {
        removal[gift.key] = 0;
      });

      lock(true);

      cart = await request(
        'cart/update.js',
        withSections({
          updates: removal,
        })
      );

      sections = cart.sections;
      changed = true;
      reason = 'not-free';
    }

    return {
      cart,
      sections,
      changed,
      reason,
    };
  }

  /* ---------- Talking to the theme ---------- */

  /**
   * Tells the theme the cart changed so it redraws
   * the cart and the bag count.
   */
  function notify(cart, sections) {
    const data = {
      source: SOURCE,
      itemCount: cart.item_count,
    };

    if (sections) {
      data.sections = sections;
    }

    document.dispatchEvent(
      new CustomEvent(CART_EVENT, {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: SOURCE,
          data,
        },
      })
    );
  }

  /**
   * Briefly stops clicks on the cart rows while
   * a cart change is being saved.
   */
  function lock(on) {
    document.querySelectorAll('cart-items-component').forEach((element) => {
      element.classList.toggle('cart-items-disabled', on);
    });
  }

  function showNotice() {
    if (!notice) return;

    document.querySelectorAll('[data-mystery-gift-message]').forEach((element) => {
      element.textContent = notice;
    });

    document.querySelectorAll('[data-mystery-gift-size]').forEach((element) => {
      if (element instanceof HTMLSelectElement) {
        element.disabled = false;
        element.value = '';
      }
    });
  }

  function finish(result) {
    if (result.reason === 'not-free') {
      notice = TEXT.notFree;
      showNotice();
      window.setTimeout(showNotice, 600);
    }
  }

  function release() {
    lock(false);
    busy = false;

    if (queued) {
      queued = false;
      schedule();
    }
  }

  /* ---------- Checking after every cart change ---------- */

  async function check() {
    if (busy) {
      queued = true;
      return;
    }

    busy = true;

    try {
      const result = await reconcile();

      if (result.changed) {
        notify(result.cart, result.sections);
        finish(result);
      }
    } catch (error) {
      console.error('[mystery-gift]', error);
    } finally {
      release();
    }
  }

  function schedule() {
    window.clearTimeout(timer);
    timer = window.setTimeout(check, 150);
  }

  document.addEventListener(CART_EVENT, (event) => {
    /**
     * Ignore our own cart signal, otherwise this would loop.
     */
    if (event.detail?.data?.source === SOURCE) return;

    notice = '';
    schedule();
  });

  /* ---------- Choosing a size adds the gift ---------- */

  async function addGift(select, attempt = 0) {
    /**
     * "Choose your size" itself adds nothing.
     */
    if (!select.isConnected || !select.value) return;

    /**
     * If a cart check is mid-flight, wait rather than
     * silently ignoring the customer's size choice.
     */
    if (busy) {
      const chosen = select.value;

      if (attempt < 20) {
        window.setTimeout(() => {
          if (select.value === chosen) {
            addGift(select, attempt + 1);
          }
        }, 150);
      }

      return;
    }

    busy = true;

    const offer = select.closest('[data-mystery-gift]');
    const message = offer?.querySelector('[data-mystery-gift-message]');
    const variantId = Number(select.value);

    const restore = (text) => {
      select.disabled = false;
      select.value = '';

      if (message) {
        message.textContent = text;
      }
    };

    select.disabled = true;

    if (message) {
      message.textContent = TEXT.adding;
    }

    try {
      let sections;

      const before = await inspect(await request('cart.js'));

      /**
       * Only add when the cart still qualifies
       * and there is no gift already present.
       */
      if (!before.gifts.length && before.qualifying >= MINIMUM) {
        lock(true);

        const added = await request(
          'cart/add.js',
          withSections({
            items: [
              {
                id: variantId,
                quantity: 1,
              },
            ],
          })
        );

        sections = added.sections;
      }

      /**
       * Reconcile immediately after the add.
       * This confirms:
       * - the cart still qualifies
       * - there is only one gift
       * - the gift is actually free
       */
      const result = await reconcile();

      notify(result.cart, result.sections || sections);
      finish(result);

      /**
       * If the cart did not redraw for any reason,
       * give the selector back after four seconds.
       */
      window.setTimeout(() => {
        if (select.isConnected && select.disabled) {
          restore('');
        }
      }, 4000);
    } catch (error) {
      console.error('[mystery-gift]', error);
      restore(TEXT.unavailable);
    } finally {
      release();
    }
  }

  const sizeSelect = (target) =>
    target instanceof HTMLSelectElement &&
    target.matches('[data-mystery-gift-size]')
      ? target
      : null;

  /**
   * Keyboard safeguard:
   * Arrowing through sizes should not add the first size
   * the customer passes over.
   */
  const BROWSING_KEYS = [
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'Home',
    'End',
    'PageUp',
    'PageDown',
  ];

  let lastBrowseKey = 0;
  let pending = 0;
  let pendingSelect = null;

  function commitPending() {
    if (!pending) return;

    window.clearTimeout(pending);
    pending = 0;

    const select = pendingSelect;
    pendingSelect = null;

    if (select) {
      addGift(select);
    }
  }

  document.addEventListener('keydown', (event) => {
    if (!sizeSelect(event.target)) return;

    if (
      BROWSING_KEYS.includes(event.key) ||
      event.key.length === 1
    ) {
      lastBrowseKey = Date.now();
    }

    if (event.key === 'Enter') {
      window.setTimeout(commitPending, 0);
    }
  });

  document.addEventListener('change', (event) => {
    const select = sizeSelect(event.target);

    if (!select) return;

    window.clearTimeout(pending);
    pending = 0;
    pendingSelect = null;

    if (Date.now() - lastBrowseKey < 250) {
      pendingSelect = select;
      pending = window.setTimeout(commitPending, 1200);
    } else {
      addGift(select);
    }
  });

  document.addEventListener('focusout', (event) => {
    if (sizeSelect(event.target)) {
      commitPending();
    }
  });

  /* ---------- On page load ---------- */

  /**
   * The page itself says whether a gift is sitting in
   * a cart that no longer qualifies.
   */
  if (
    document.querySelector(
      '[data-mystery-gift][data-state="fix"]'
    )
  ) {
    schedule();
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      schedule();
    }
  });
})();